import { IPagination } from "app/types";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { TICKET_API_URL } from "utils/constants";
import { handleApiError } from "utils/errorHandler";
import { request } from "utils/request";
import toastService from "utils/toast";
import { IComponent } from "../types";
import { selectComponentFormData, selectPagination } from "./selector";
import { componentActions } from "./slice";

export function* createComponentRequest(action: {
  payload: { projectId: string; callback?: () => void };
}) {
  try {
    yield delay(500);
    const { projectId, callback } = action.payload;
    const formData: IComponent = yield select(selectComponentFormData);
    const { _id: _componentId, ...componentPayload } = formData;
    yield put(componentActions.setButtonLoading(true));
    const requestURL = `${TICKET_API_URL}component/create`;
    const options = {
      method: "POST",
      body: JSON.stringify({
        ...componentPayload,
        projectId,
      }),
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(componentActions.setButtonLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }

    if (callback) {
      yield call(callback);
    }
    toastService.success(data.message);
  } catch (err) {
    handleApiError(err);
  } finally {
    yield put(componentActions.setButtonLoading(false));
  }
}

export function* updateComponentRequest(action: {
  payload: { projectId: string; callback?: () => void };
}) {
  try {
    yield delay(500);
    const { callback } = action.payload;
    const formData: IComponent = yield select(selectComponentFormData);
    const requestURL = `${TICKET_API_URL}component/update/${formData._id}`;
    const { _id: _componentId, ...componentPayload } = formData;
    const options = {
      method: "PUT",
      body: JSON.stringify(componentPayload),
    };
    yield put(componentActions.setButtonLoading(true));
    const { data, err } = yield call(request, requestURL, options);
    yield put(componentActions.setButtonLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }

    if (callback) {
      yield call(callback);
    }

    toastService.success(data.message);
  } catch (err) {
    yield put(componentActions.setButtonLoading(false));
    handleApiError(err);
  }
}

export function* fetchComponentDetailsRequest(action: { payload: string }) {
  try {
    yield delay(500);
    const componentId = action.payload;
    const requestURL = `${TICKET_API_URL}component/${componentId}`;
    const options = { method: "GET" };
    yield put(componentActions.setFieldLoading(true));
    const { data, err } = yield call(request, requestURL, options);
    yield put(componentActions.setFieldLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    yield put(componentActions.setComponentDetail(data.data));
  } catch (err) {
    yield put(componentActions.setFieldLoading(false));
    handleApiError(err);
    return;
  }
}

export function* fetchComponentsListRequest(action: {
  payload: {
    projectId: string;
  };
}) {
  try {
    const { projectId } = action.payload;
    const pagination = (yield select(selectPagination)) as IPagination;

    const { page, perPage } = pagination;

    const requestURL = `${TICKET_API_URL}component/list/${projectId}?page=${page}&perPage=${perPage}`;
    const options = {
      method: "GET",
    };
    yield put(componentActions.setLoading(true));
    const { data, err } = yield call(request, requestURL, options);
    yield put(componentActions.setLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }

    const results = data.data;
    const meta = data?._metadata?.pagination;
    yield put(componentActions.setComponentsList(results));
    if (meta) {
      yield put(componentActions.setPagination(meta));
    }
  } catch (err) {
    yield put(componentActions.setLoading(false));
    handleApiError(err);
    return;
  }
}

export function* projectComponentSaga() {
  yield takeLatest(componentActions.createComponent, createComponentRequest);
  yield takeLatest(
    componentActions.fetchComponentsList,
    fetchComponentsListRequest,
  );
  yield takeLatest(componentActions.updateComponent, updateComponentRequest);
  yield takeLatest(
    componentActions.fetchComponentDetails,
    fetchComponentDetailsRequest,
  );
}
